import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Link,
  FormControlLabel,
  Checkbox,
  Divider,
  Paper,
  Avatar,
  CssBaseline,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";

// Animated background component
const AnimatedBackground = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  background: linear-gradient(-45deg, #25d366, #128c7e, #075e54, #34b7f1);
  background-size: 400% 400%;
  animation: ${keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  `} 15s ease infinite;
`;

const RegistrationPage = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    // Handle registration logic here
  };

  return (
    <>
      <CssBaseline />
      <AnimatedBackground />
      <Container component="main" maxWidth="sm" sx={{ position: "relative" }}>
        <Paper
          elevation={6}
          sx={{
            p: 4,
            mt: 8,
            mb: 4,
            borderRadius: 3,
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
              Sign up for WhatsApp Tech Service
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <WhatsAppIcon color="success" sx={{ fontSize: 40, mr: 1 }} />
              <Typography variant="body1">
                Get started with our premium tech support via WhatsApp
              </Typography>
            </Box>

            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              sx={{ mt: 3 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    autoComplete="given-name"
                    name="firstName"
                    required
                    fullWidth
                    size="small"
                    id="firstName"
                    label="First Name"
                    autoFocus
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    id="lastName"
                    label="Last Name"
                    name="lastName"
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    id="phone"
                    label="Contact Number"
                    name="phone"
                    autoComplete="tel"
                    placeholder="+91 234 567 8900"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    size="small"
                    name="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    size="small"
                    control={
                      <Checkbox
                        name="acceptTerms"
                        color="primary"
                        checked={formData.acceptTerms}
                        onChange={handleChange}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I agree to the{" "}
                        <a
                          href="https://www.smartgrowthai.com/terms"
                          color="primary"
                          target="_blank"
                        >
                          Terms of Service
                        </a>{" "}
                        and{" "}
                        <a
                          href="https://www.smartgrowthai.com/privacypolicy"
                          color="primary"
                          target="_blank"
                        >
                          Privacy Policy
                        </a>
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="success"
                size="large"
                sx={{
                  mt: 3,
                  mb: 2,
                  background:
                    "linear-gradient(45deg, #25d366 30%, #128c7e 90%)",
                  boxShadow: "0 3px 5px 2px rgba(37, 211, 102, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(45deg, #128c7e 30%, #075e54 90%)",
                  },
                }}
                disabled={!formData.acceptTerms}
                startIcon={<WhatsAppIcon />}
              >
                Register & Connect via WhatsApp
              </Button>

              <Divider sx={{ my: 2 }}>OR</Divider>

              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Typography variant="body2">
                  Already have an account?{" "}
                  <Link href="/login" variant="body2" color="primary">
                    Sign in
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Typography
            variant="body2"
            color="white"
            sx={{ textShadow: "1px 1px 2px rgba(0,0,0,0.5)" }}
          >
            By registering, you'll get access to our 24/7 tech support via
            WhatsApp
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default RegistrationPage;
